import React from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { FiMapPin, FiClock, FiPhoneCall, FiMail } from 'react-icons/fi';

const mapAddress = "Str. Somesului 2D, Florești, Romania";
const mapUrl = `https://www.google.com/maps?q=${encodeURIComponent(mapAddress)}&output=embed`;

const LocationCard = ({ icon, title, description }) => {
    return (
        <div className="flex items-start p-4">
            <div className="text-arcdom-gold text-2xl mr-4 mt-1 flex-shrink-0">
                {icon}
            </div>
            <div>
                <h3 className="text-lg font-medium text-arcdom-navy mb-1">{title}</h3>
                <p className="text-gray-600 break-words">{description}</p>
            </div>
        </div>
    );
};

const Location = () => {
    const [ref, inView] = useInView({
        triggerOnce: true,
        threshold: 0.1,
    });

    const itemVariants = {
        hidden: { opacity: 0, x: -30 },
        visible: { opacity: 1, x: 0, transition: { duration: 0.6, ease: "easeOut" } }
    };
    const itemVariantsRight = {
        hidden: { opacity: 0, x: 30 },
        visible: { opacity: 1, x: 0, transition: { duration: 0.6, ease: "easeOut" } }
    };

    return (
        <section id="location" className="py-20 bg-gray-50">
            <div className="container mx-auto px-4">
                <motion.div
                    ref={ref}
                    initial={{ opacity: 0, y: 20 }}
                    animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
                    transition={{ duration: 0.6 }}
                    className="text-center mb-16"
                >
                    <h2 className="text-4xl font-bold text-arcdom-navy mb-4">Visit Our Office</h2>
                    <div className="w-20 h-1 bg-arcdom-gold mx-auto mb-6"></div>
                    <p className="max-w-2xl mx-auto text-gray-600">
                        Our team is ready to assist you with any inquiries about our projects.
                        Feel free to visit us at our central office in Cluj-Napoca.
                    </p>
                </motion.div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-center overflow-hidden">
                    <motion.div
                        initial="hidden"
                        whileInView="visible"
                        viewport={{once: true, amount: 0.2}}
                        variants={itemVariants}
                        className="h-96 lg:h-[500px] w-full bg-gray-300 rounded-lg overflow-hidden shadow-lg"
                    >
                        <iframe
                            src={mapUrl}
                            width="100%"
                            height="100%"
                            style={{border: 0}}
                            allowFullScreen={false}
                            loading="lazy"
                            referrerPolicy="no-referrer-when-downgrade"
                            title="Arcdom Office Location"
                        ></iframe>
                    </motion.div>

                    <motion.div
                        initial="hidden"
                        whileInView="visible"
                        viewport={{once: true, amount: 0.2}}
                        variants={itemVariantsRight}
                        className="bg-white p-6 md:p-8 rounded-lg shadow-lg overflow-hidden"
                    >
                        <h3 className="text-2xl font-semibold text-arcdom-navy mb-6">Contact Information</h3>

                        <div className="space-y-1">
                            <LocationCard
                                icon={<FiMapPin/>}
                                title="Address"
                                description="Str. Somesului 2 D Sc. 3 Ap. 3, Florești, Romania"
                            />
                            <LocationCard
                                icon={<FiClock/>}
                                title="Business Hours"
                                description="Monday - Friday: 9:00 AM - 6:00 PM"
                            />
                            <LocationCard
                                icon={<FiPhoneCall/>}
                                title="Phone"
                                description="(0754) 041 648"
                            />
                            <LocationCard
                                icon={<FiMail/>}
                                title="Email"
                                description="office@arcdom.ro"
                            />
                        </div>
                    </motion.div>
                </div>
            </div>
        </section>
    );
};

export default Location;
